import React, { useState, useEffect, useMemo } from "react";
import { DifficultySelector } from "./DifficultySelector";
import { DateTimeSelector } from "./DateTimeSelector";
import "./ScheduleTest.css";

export function ScheduleTest({ selectedEmails, jobDetails, onClose, selectedTestCard, jobId }) {
  const [difficulty, setDifficulty] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [testIds, setTestIds] = useState([]);
  const [language, setLanguage] = useState("");
  const [topic, setTopic] = useState(jobDetails?.job_title || "Default");
  const [testTimes, setTestTimes] = useState({});
  const [isScheduling, setIsScheduling] = useState(false);
  const [schedulingProgress, setSchedulingProgress] = useState({
    currentTest: null,
    completedTests: [],
    totalTests: 0
  });
  
  // Helper to get test type from name
  const getTestType = (testName) => {
    const tn = testName.toLowerCase();
    if (tn.includes("screening")) return "screening";
    if (tn.includes("coding")) return "coding";
    if (tn.includes("all")) return "all-test";
    if (tn.includes("personality")) return "personality";
    if (tn.includes("behavioral") || tn.includes("behavioural")) return "behavioral";
    if (tn.includes("communication")) return "communication";
    return "other";
  };

  // Sort tests in desired order: screening → coding → personality → behavioral → communication
  const sortedTests = useMemo(() => {
    const order = {
      screening: 1,
      coding: 2,
      personality: 3,
      "all-test": 4,
      behavioral: 5,
      communication: 6
    };
    return [...selectedTestCard].sort((a, b) => {
      const aType = getTestType(typeof a === 'string' ? a : a.name);
      const bType = getTestType(typeof b === 'string' ? b : b.name);
      return (order[aType] || 99) - (order[bType] || 99);
    });
  }, [selectedTestCard]);

  // Format date to YYYY-MM-DDTHH:mm format
  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    
    try {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch (e) {
      return dateString;
    }
  };

  // Function to handle time change for current test
  const handleTimeChange = (selectedTime, index) => {
    const newTimes = { ...testTimes };
    newTimes[index] = selectedTime;
    
    // Update subsequent test times with 30-minute increments
    for (let i = index + 1; i < sortedTests.length; i++) {
      if (newTimes[i - 1]) {
        const prevTime = new Date(newTimes[i - 1]);
        prevTime.setMinutes(prevTime.getMinutes() + 30);
        newTimes[i] = formatDateForInput(prevTime);
      }
    }
    
    setTestTimes(newTimes);
  };

  // Initialize times when component mounts
  useEffect(() => {
    if (sortedTests.length > 0 && Object.keys(testTimes).length === 0) {
      const initialTimes = {};
      sortedTests.forEach((_, index) => {
        initialTimes[index] = "";
      });
      setTestTimes(initialTimes);
    }
  }, [sortedTests]);

  const isOnlyPersonalityOrBehavioral = useMemo(() => sortedTests.every(tc => {
    const testName = typeof tc === 'string' ? tc : tc?.name || '';
    return testName.toLowerCase().includes("personality") ||
      testName.toLowerCase().includes("behavioral") || 
      testName.toLowerCase().includes("all");
  }), [sortedTests]);

  const hasPersonalityOrBehavioral = useMemo(() => sortedTests.some(tc => {
    const testName = typeof tc === 'string' ? tc : tc?.name || '';
    return testName.toLowerCase().includes("personality") ||
      testName.toLowerCase().includes("behavioral") ||
      testName.toLowerCase().includes("behaviour");
  }), [sortedTests]);

  const hasMixedTestTypes = hasPersonalityOrBehavioral && !isOnlyPersonalityOrBehavioral;

  const handleButtonClick = async () => {
    // Validate all required fields
    if (!selectedEmails) {
      alert("Please select at least one user.");
      return;
    }

    // Validate all test times are set
    const missingTimes = sortedTests.filter((_, i) => !testTimes[i]);
    if (missingTimes.length > 0) {
      alert("Please set times for all tests");
      return;
    }

    let finalDifficulty = difficulty;
    if (isOnlyPersonalityOrBehavioral) {
      finalDifficulty = "Medium";
    }

    if (!finalDifficulty && !isOnlyPersonalityOrBehavioral) {
      alert("Please select a difficulty level for the tests.");
      return;
    }

    try {
      setIsScheduling(true);
      setSchedulingProgress({
        currentTest: null,
        completedTests: [],
        totalTests: sortedTests.length
      });
      const testIds = [];

      for (const [index, testCard] of sortedTests.entries()) {
        const testName = typeof testCard === 'string' ? testCard : testCard?.name || '';
        const testTime = testTimes[index];

        // Update progress
        setSchedulingProgress(prev => ({
          ...prev,
          currentTest: {
            name: testName,
            type: getTestType(testName)
          }
        }));

        const formData = new URLSearchParams();
        formData.append("jobId", jobId);
        formData.append("scheduledTime", testTime);
        formData.append("selected_users", selectedEmails);
        formData.append("topic", topic);

        if (testName.toLowerCase().includes("personality") || testName.toLowerCase().includes("behavioral")) {
          formData.append("difficulty", "Medium");
        } else {
          formData.append("difficulty", finalDifficulty);
        }

        formData.append("test_type", testCard);

        // Add language only for coding tests
        if (testName.toLowerCase().includes("coding")) {
          if (!language) {
            alert("Please select a language for the coding test.");
            return;
          }
          formData.append("language", language);
        }

        const response = await fetch("/api/schedule_test_jobId", {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: formData.toString(),
        });

        const data = await response.json();
        testIds.push({
          testId: data.test_id,
          testCard,
          testName: typeof testCard === 'string' ? testCard : testCard?.name || 'Test',
          scheduledTime: testTime
        });

        setSchedulingProgress(prev => ({
          ...prev,
          completedTests: [...prev.completedTests, getTestType(testName)],
          currentTest: null
        }));
      }

      setTestIds(testIds);
      setShowPopup(true);
    } catch (error) {
      console.error("Error scheduling the test:", error);
      alert("An error occurred while scheduling the test.");
    } finally {
      setIsScheduling(false);
    }
  };

  const closePopup = () => {
    window.location.reload();
    setShowPopup(false);
  };

  const formatDateTime = (datetime) => {
    const date = new Date(datetime);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTestIcon = (testType) => {
    switch(testType) {
      case 'screening': return '🔍';
      case 'coding': return '💻';
      case 'personality': return '🧠';
      case 'behavioral': return '📊';
      case 'all-test': return '🧩';
      case 'communication': return '🗣️';
      default: return '📝';
    }
  };

  const getTestDisplayName = (testType) => {
    switch(testType) {
      case 'screening': return 'Screening';
      case 'coding': return 'Coding Challenge';
      case 'personality': return 'Personality Assessment';
      case 'behavioral': return 'Behavioral Assessment';
      case 'all-test': return 'Personality & Behavioral';
      case 'communication': return 'Communication Test';
      default: return 'Assessment';
    }
  };

  return (
    <>
      {!showPopup && (
        <div className="modal-overlay">
          <div className="modern-schedule-modal">
            <div className="modern-modal-header">
              <h2>Schedule Tests</h2>
              <button className="close-button" onClick={onClose}>×</button>
            </div>
            
            <div className="schedule-form">
              {hasMixedTestTypes && (
                <div className="mixed-test-note">
                  <span className="note-icon">ℹ️</span>
                  <p>Personality/Behavioral tests use Medium difficulty</p>
                </div>
              )}

              <div className="difficulty-section">
                <label>Test Difficulty Level</label>
                <div className="difficulty-container">
                  <DifficultySelector 
                    onChange={setDifficulty} 
                    value={difficulty}
                    className={isOnlyPersonalityOrBehavioral ? "disabled" : ""}
                    disabled={isOnlyPersonalityOrBehavioral}
                  />
                  {isOnlyPersonalityOrBehavioral && (
                    <div className="disabled-note">(Set to Medium Difficulty)</div>
                  )}
                </div>
              </div>

              <div className="test-cards-container">
                {sortedTests.map((testCard, index) => {
                  const rawName = typeof testCard === "string" ? testCard : testCard.name;
                  const testType = getTestType(rawName);

                  // Display name
                  const displayName = (() => {
                    if (testType === "all-test") return "Personality & Behavioral";
                    if (testType === "screening") return "Screening";
                    return testType.charAt(0).toUpperCase() + testType.slice(1);
                  })();

                  return (
                    <div key={index} className={`test-card-modern ${testType}`}>
                      <div className="test-card-header">
                        <span className="test-icon">{getTestIcon(testType)}</span>
                        <h3>{displayName} Test</h3>
                      </div>
                      <div className="test-card-body">
                        {testType === 'screening' && (
                          <div className="test-card-field">
                            <label>Test Topic</label>
                            <input
                              type="text"
                              className="topic-input"
                              value={topic}
                              onChange={(e) => setTopic(e.target.value)}
                              placeholder="Enter topic"
                            />
                          </div>
                        )}
                        
                        {testType === 'coding' && (
                          <div className="test-card-field">
                            <label>Programming Language</label>
                            <select
                              className="language-select"
                              onChange={(e) => setLanguage(e.target.value)}
                              value={language}
                              required
                            >
                              <option value="" disabled>Select Language</option>
                              <option value="C++">C++</option>
                              {/* "C" added next to C++ — grouped systems-languages
                                  together so recruiters see them as a pair. */}
                              <option value="C">C</option>
                              <option value="Python">Python</option>
                              <option value="JavaScript">JavaScript</option>
                              <option value="NodeJS">NodeJS</option>
                              <option value="Java">Java</option>
                            </select>
                          </div>
                        )}
                        
                        <div className="test-card-field">
                          <DateTimeSelector 
                            value={testTimes[index] || ''}
                            onChange={(value) => handleTimeChange(value, index)}
                            required
                          />
                          {index > 0 && testTimes[index] && (
                            <div className="time-note">
                              <span>⏱️</span> 
                              <span>Scheduled 30 min after previous</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="modal-actions">
              <button 
                className={`schedule-button ${isScheduling ? 'loading' : ''}`} 
                onClick={handleButtonClick}
                disabled={isScheduling}
              >
                {isScheduling ? (
                  <>
                    <div className="spinner"></div>
                    <span>Scheduling Tests...</span>
                  </>
                ) : "Schedule All Tests"}
              </button>
              <button className="cancel-button" onClick={onClose}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Progress Loader Overlay */}
      {isScheduling && (
        <div className="progress-overlay">
          <div className="progress-container">
            <div className="progress-header">
              <h2>Scheduling Tests</h2>
              <p>Please wait while we schedule your tests...</p>
            </div>
            
            <div className="progress-steps">
              {sortedTests.map((testCard, index) => {
                const testName = typeof testCard === 'string' ? testCard : testCard?.name || '';
                const testType = getTestType(testName);
                const displayName = getTestDisplayName(testType);
                
                const isCurrent = schedulingProgress.currentTest?.type === testType;
                const isCompleted = schedulingProgress.completedTests.includes(testType);
                const status = isCompleted ? 'completed' : isCurrent ? 'processing' : 'pending';
                
                return (
                  <div key={index} className={`progress-step ${status}`}>
                    <div className="step-icon">
                      {status === 'completed' ? '✓' : status === 'processing' ? '⏳' : index + 1}
                    </div>
                    <div className="step-details">
                      <div className="step-name">{displayName} Test</div>
                      <div className="step-status">
                        {status === 'completed' ? 'Scheduled' : 
                         status === 'processing' ? 'Scheduling now...' : 
                         'Waiting to schedule'}
                      </div>
                    </div>
                    {status === 'processing' && (
                      <div className="step-loader">
                        <div className="step-spinner"></div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            
            <div className="progress-time-note">
              <p>Each test takes approximately 10-15 seconds to schedule</p>
            </div>
          </div>
        </div>
      )}

      {showPopup && (
        <div className="popup-overlay">
          <div className="success-popup">
            <button className="close-popup" onClick={closePopup}>×</button>
            <div className="success-content">
              <div className="success-header">
                <div className="success-icon">✅</div>
                <h2>Tests Scheduled!</h2>
              </div>
              
              <p className="success-message">
                The following tests have been scheduled:
              </p>
              
              <div className="scheduled-tests-grid">
                {testIds.map(({ testId, testName, scheduledTime }) => {
                  let formattedTestName = "";
                  let testDetails = "";

                  switch (testName) {
                    case "/screening":
                      formattedTestName = "Technical Test";
                      testDetails = "Assesses technical skills";
                      break;
                    case "/communication":
                      formattedTestName = "Communication Test";
                      testDetails = "Evaluates communication skills";
                      break;
                    case "/personality-test":
                      formattedTestName = "Personality Assessment";
                      testDetails = "Evaluates personality traits";
                      break;
                    case "/behavioral-test":
                      formattedTestName = "Behavioral Assessment";
                      testDetails = "Analyzes behavioral responses";
                      break;
                    case "/icoding":
                      formattedTestName = "Coding Challenge";
                      testDetails = "Tests programming skills";
                      break;
                    case "/all-test":
                      formattedTestName = "Personality & Behavioral";
                      testDetails = "Combined personality/behavioral";
                      break;
                    default:
                      formattedTestName = testName;
                      testDetails = "Assessment details";
                  }

                  return (
                    <div key={testId} className="scheduled-test-item">
                      <div className="test-name">{formattedTestName}</div>
                      <div className="test-details">{testDetails}</div>
                      <div className="test-time">
                        <span className="time-icon">🕒</span>
                        {formatDateTime(scheduledTime)}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              <div className="success-actions">
                <button className="done-button" onClick={closePopup}>
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}